import { StateCreator } from 'zustand'
import { no_parent, NO_PARENT, TreeData, TreeNode } from '../tree-data/tree-data'
import { item_id, node_id } from '../common'
import {
  AddingState,
  CheckState, findNodeIdsByItemId, nodeIdToItemIdThrows, TreeClientState, TreeStoreActions, TreeStoreType,
} from './tree-store'
import { produce } from 'immer'
import { TreeEffectInput } from './effects'
import { v4 as uuidv4 } from "uuid"
import uniqueId from "lodash.uniqueid"
import { assertNever } from '../../../../../utils/assert-never'

const EFFECT_QUEUE_LIMIT = 10_000

export const createTreeStateActions
  = <M1 extends [], M2 extends []>()
  : StateCreator<TreeStoreType, M1, M2, TreeStoreActions> => ((set, get) => {
  return {
    setItemSelection: (selected: item_id[]) => {
      set(produce((state: TreeStoreType) => {
        state.client.selectedItems = new Set<node_id>(selected)
      }))
    },
    select: (id: node_id, mode: "single" | "toggle" | "range") => {
      set(state => ({
        client: {
          ...state.client,
          ...handleSelectionChange(state, id, mode)
        }
      }))
    },
    toggleNodeChecked: (id: node_id, checked: boolean) => {
      set(produce((state: TreeStoreType) => {
        state.client.checkedItems = setNodeLineageValue(state.treeData, state.client.checkedItems, id, checked)
        state.computed.checkStateByNode = computeCheckState(state.treeData, state.client.checkedItems)
      }))
    },
    setCheckedItems: (checkedIds: item_id[]) => {
      set(produce((state: TreeStoreType) => {
        state.client.checkedItems = {}
        for (const nodeId of state.treeData.rootNodeIds) {
          state.client.checkedItems = setNodeLineageValue(state.treeData, state.client.checkedItems, nodeId, false)
        }
        const checkedNodeIds = checkedIds.flatMap(id => findNodeIdsByItemId(state.treeData.nodesById, id))
        for (const nodeId of checkedNodeIds) {
          state.client.checkedItems = setNodeLineageValue(state.treeData, state.client.checkedItems, nodeId, true)
        }

        state.computed.checkStateByNode = computeCheckState(state.treeData, state.client.checkedItems)
      }))
    },
    toggleNode: (toggleId: string, id: node_id, toggled: boolean) => {
      set(produce((state: TreeStoreType) => {
        const byItem = state.client.toggledItems[toggleId] ?? {}
        state.client.toggledItems[toggleId] = setNodeLineageValue(state.treeData, byItem, id, toggled)
      }))
    },
    setToggledItems: (toggleId: string, toggledIds: item_id[]) => {
      set(produce((state: TreeStoreType) => {
        let byItem: Record<item_id, boolean> = {}
        for (const nodeId of state.treeData.rootNodeIds) {
          byItem = setNodeLineageValue(state.treeData, byItem, nodeId, false)
        }
        const toggledNodeIds = toggledIds.flatMap(id => findNodeIdsByItemId(state.treeData.nodesById, id))
        for (const nodeId of toggledNodeIds) {
          byItem = setNodeLineageValue(state.treeData, byItem, nodeId, true)
        }
        state.client.toggledItems[toggleId] = byItem
      }))
    },
    toggleNodeExpanded: (id: node_id, expanded: boolean) => {
      set(produce((state: TreeStoreType) => {
        state.client.expandedNodes[id] = expanded
        state.computed.visibleNodeIds = computeVisibleNodeIds(state.treeData, state.client.expandedNodes)
      }))
    },
    startNodeEditing: (id: node_id, userData?: unknown) => {
      set(produce((state: TreeStoreType) => {
        if (state.client.editingStates.find(s => s.nodeId === id)) return
        state.client.editingStates.push({
          nodeId: id,
          userData
        })
      }))
    },
    stopNodeEditing: (id: node_id) => {
      set(produce((state: TreeStoreType) => {
        const index = state.client.editingStates.findIndex(s => s.nodeId === id)
        if (index === -1) return
        state.client.editingStates.splice(index, 1)
      }))
    },
    startAdding: (parentItemId: item_id | no_parent, userData?: unknown) => {
      const addingState: AddingState = {
        addingStateId: uniqueId(),
        parentItemId,
        userData
      }
      set(produce((state: TreeStoreType) => {
        state.client.addingStates.push(addingState)
      }))
      return addingState
    },
    stopAdding: (addingStateId) => {
      set(produce((state: TreeStoreType) => {
        const index = state.client.addingStates.findIndex(s => s.addingStateId === addingStateId)
        if (index === -1) return
        state.client.addingStates.splice(index, 1)
      }))
    },
    selectOnceWhenInTreeData(nodeId) {
      set(produce((state: TreeStoreType) => {
        const id = uuidv4()
        state.client.changeQueue.push({
          type: 'select-once-when-in-tree-data',
          id,
          itemId: nodeId,
          settled: () => set(produce((draft: TreeStoreType) => {
            const index = draft.client.changeQueue.findIndex(change => change.id === id)
            if (index === -1) return
            draft.client.changeQueue.splice(index, 1)
          }))
        })
      }))
    },
    revealNode: (id: node_id, scrollBehaviour: "smooth" | "instant") => {
      expandNode(id)
      enqueueEffect({
        type: "reveal",
        nodeId: id,
        scrollBehaviour
      })
    },
    clearNodeToReveal: () => {
      set(state => ({
        client: {
          ...state.client,
          nodeToReveal: null
        }
      }))
    },
    computeCheckState: () => {
      set(state => ({
        computed: {
          ...state.computed,
          checkStateByNode: computeCheckState(state.treeData, state.client.checkedItems)
        }
      }))
    },
    computeVisibleNodeIds: () => {
      set(state => ({
        computed: {
          ...state.computed,
          visibleNodeIds: computeVisibleNodeIds(state.treeData, state.client.expandedNodes)
        }
      }))
    },
    applyClientChangesInQueue() {
      for (const change of get().client.changeQueue) {
        try {
          switch (change.type) {
            case 'select-once-when-in-tree-data':
              const nodeIds = findNodeIdsByItemId(get().treeData.nodesById, change.itemId)
              if (nodeIds.length > 1) console.warn(`multiple nodes found for item ${change.itemId}`)
              if (nodeIds.length) {
                expandNode(nodeIds[0])
                get().actions.select(nodeIds[0], 'single')
              }
              change.settled()
              break
            case 'workaround for `assertNever` only required when union of one':
              break
            default: assertNever(change)
          }
        } catch(error) {
          console.error(error)
        }
      }
    },
  }

  function expandNode(nodeId: node_id) {
      set(produce((state: TreeStoreType) => {
        state.client.expandedNodes = expandNodeAncestors(state.treeData, state.client.expandedNodes, nodeId)
        state.computed.visibleNodeIds = computeVisibleNodeIds(state.treeData, state.client.expandedNodes)
      }))
  }

  function enqueueEffect(effect: TreeEffectInput) {
    set(produce((state: TreeStoreType) => {
      if (state.client.effectQueue.length > EFFECT_QUEUE_LIMIT) {
        console.error("Too many simultaneous Tree++ effects queued. Ignoring next queued effect: " + effect.type)
        return;
      }

      const uuid = uuidv4()
      state.client.effectQueue.push({
        ...effect,
        uuid,
        settled: () => {
          set(produce((state: TreeStoreType) => {
            const index = state.client.effectQueue.findIndex(e => e.uuid === uuid)
            if (index !== 0) console.error('Effects should be settled in order!')
            state.client.effectQueue.splice(index, 1)
          }))
          effect.settled?.()
        }
      })
    }))
  }
})

function handleSelectionChange(
  state: TreeStoreType,
  nodeId: node_id,
  mode: "single" | "toggle" | "range",
): Partial<Pick<TreeClientState, 'selectedItems' | 'lastSelectedNode'>> {
  const alreadySelectedItems = state.client.selectedItems
  const lastSelectedNode = state.client.lastSelectedNode
  // Abort if selected ID does not exist,
  // which can happen if the selected item has just been deleted in the tree
  if (!(nodeId in state.treeData.nodesById)) {
    return {
      selectedItems: alreadySelectedItems,
      lastSelectedNode
    }
  }

  const itemId = nodeIdToItemIdThrows(state.treeData, nodeId)
  const ids = state.computed.visibleNodeIds
  const isAlreadySelected = alreadySelectedItems.has(itemId)

  if (mode === "range" && lastSelectedNode) {
    const lastIndex = ids.indexOf(lastSelectedNode)
    const curIndex = ids.indexOf(nodeId)
    if (curIndex === -1 || lastIndex === -1) return {}

    const startPos = Math.min(lastIndex, curIndex)
    const endPos = Math.max(lastIndex, curIndex)
    const toSelectItems = ids
      .slice(startPos, endPos + 1)
      .map(id => nodeIdToItemIdThrows(state.treeData, id))

    return {
      selectedItems: new Set([...alreadySelectedItems, ...toSelectItems]),
      lastSelectedNode: nodeId
    }
  } else if (mode === "toggle") {
      const selectedItems = new Set(state.client.selectedItems)
      if (isAlreadySelected) {
        selectedItems.delete(itemId)
        return { selectedItems }
      } else {
        selectedItems.add(itemId)
        return { selectedItems, lastSelectedNode: nodeId }
      }
  } else {
    return {
      selectedItems: isAlreadySelected ? new Set() : new Set([ itemId ]),
      lastSelectedNode: isAlreadySelected ? null : nodeId
    }
  }
}

function setNodeLineageValue(
  treeData: TreeData,
  byItem: Record<item_id, boolean>,
  nodeId: node_id,
  value: boolean,
): Record<item_id, boolean> {
  const node = treeData.nodesById[nodeId]
  byItem[node.itemId] = value
  for (const childId of node.childrenIds) {
    setNodeLineageValue(treeData, byItem, childId, value)
  }
  return byItem
}

function expandNodeAncestors(treeData: TreeData, expandedNodes: Record<node_id, boolean>, id: node_id) {
  const node = treeData.nodesById[id]
  if (node.parentId === NO_PARENT) return expandedNodes

  expandedNodes[node.parentId] = true
  expandNodeAncestors(treeData, expandedNodes, node.parentId)

  return expandedNodes
}

function computeCheckState(treeData: TreeData, checkedItems: Record<item_id, boolean>): Record<node_id, CheckState> {
  const checkState: Record<node_id, CheckState> = {}

  function isChecked(node: TreeNode) {
    if (node.itemId in checkedItems) {
      return checkedItems[node.itemId]
    } else {
      const item = treeData.itemsById[node.itemId]
      return item.defaultChecked
    }
  }

  function computeCheckStateRecurse(nodeIds: node_id[]) {
    for (const nodeId of nodeIds) {
      const node = treeData.nodesById[nodeId]

      if (node.childrenIds.length === 0) { // Leaf node
        checkState[nodeId] = isChecked(node)
          ? "checked"
          : "unchecked"
      } else {
        // Internal node; depth first recurse
        computeCheckStateRecurse(node.childrenIds)

        const childStates = node.childrenIds.map(id => checkState[id])
        if (childStates.every(state => state === "checked")) {
          checkState[nodeId] = "checked"
        } else if (childStates.every(state => state === "unchecked")) {
          checkState[nodeId] = "unchecked"
        } else {
          checkState[nodeId] = "indeterminate"
        }
      }
    }
  }

  computeCheckStateRecurse(treeData.rootNodeIds)

  return checkState
}

function computeVisibleNodeIds(treeData: TreeData, expandedNodes: Record<node_id, boolean>) {
  const visibleNodeIds: node_id[] = []

  function recurse(nodeIds: node_id[]) {
    for (const nodeId of nodeIds) {
      visibleNodeIds.push(nodeId)
      if (!expandedNodes[nodeId]) continue
      const childrenIds = treeData.nodesById[nodeId].childrenIds
      recurse(childrenIds)
    }
  }

  recurse(treeData.rootNodeIds)

  return visibleNodeIds
}